import React, { useEffect, useMemo, useState } from "react";
import { Offcanvas, OffcanvasBody, OffcanvasHeader, Alert } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../store";
import { formatDate, toVND } from "../../helpers/helpers";
import { updateOrderStatus } from "../../helpers/backend_helper";
import { clearOrderDetail } from "../../slices/Order/detail/reducer";
import { getOrderDetail } from "../../slices/thunks";
import ConfirmModal from "./ConfirmModal";
import {
    getOrderReceivingPlaceLabel,
    getOrderStatusClassName,
    getOrderStatusLabel,
    getOrderTypeLabel,
} from "../../constants/orderMappings";

type OrderAction = {
    label: string;
    nextStatus: string;
    type: "primary" | "danger";
};

interface OrderDetailOffcanvasProps {
    isOpen: boolean;
    orderId?: string | number;
    onClose: () => void;
    onActionComplete?: () => void;
}

const OrderDetailOffcanvas: React.FC<OrderDetailOffcanvasProps> = ({
    isOpen,
    orderId,
    onClose,
    onActionComplete,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const { order, loading, error } = useSelector((state: any) => state.OrderDetail ?? {});
    const [pendingAction, setPendingAction] = useState<OrderAction | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen && orderId) {
            dispatch(getOrderDetail(orderId));
        }
    }, [dispatch, isOpen, orderId]);

    useEffect(() => {
        if (!isOpen) {
            dispatch(clearOrderDetail());
            setActionError(null);
            setIsSubmitting(false);
        }
    }, [dispatch, isOpen]);

    useEffect(() => {
        if (isOpen || pendingAction) {
            return;
        }

        const resetBodyScroll = window.setTimeout(() => {
            const hasOpenOverlay = document.querySelector(".modal.show, .offcanvas.show");
            if (!hasOpenOverlay) {
                document.body.classList.remove("modal-open");
                document.body.style.removeProperty("overflow");
                document.body.style.removeProperty("padding-right");
            }
        }, 0);

        return () => window.clearTimeout(resetBodyScroll);
    }, [isOpen, pendingAction]);

    const normalizedStatus = useMemo(() => {
        const status = order?.status ?? order?.order_status ?? order?.state;
        return typeof status === "string" ? status.toLowerCase() : "";
    }, [order]);

    const statusLabel = useMemo(() => getOrderStatusLabel(normalizedStatus), [normalizedStatus]);
    const statusClassName = useMemo(() => getOrderStatusClassName(normalizedStatus), [normalizedStatus]);
    const orderTypeLabel = useMemo(
        () => getOrderTypeLabel(order?.order_type ?? order?.orderType),
        [order],
    );
    const receivingPlaceLabel = useMemo(
        () => getOrderReceivingPlaceLabel(order?.receiving_place ?? order?.receivingPlace),
        [order],
    );

    const paymentStatus = useMemo(() => {
        const rawStatus = order?.payment_status ?? (order?.is_paid ? "paid" : "unpaid");
        if (typeof rawStatus !== "string") {
            return "--";
        }
        const normalizedPaymentStatus = rawStatus.toLowerCase();
        const paymentStatusMap: Record<string, string> = {
            paid: "Đã thanh toán",
            unpaid: "Chưa thanh toán",
            pending: "Đang xử lý",
            failed: "Thanh toán thất bại",
            refunded: "Đã hoàn tiền",
        };
        return paymentStatusMap[normalizedPaymentStatus] ?? rawStatus;
    }, [order]);

    const formatPaymentStatus = (value?: string) => {
        if (!value) return "--";
        const normalized = value.toLowerCase();
        const paymentStatusMap: Record<string, string> = {
            paid: "Đã thanh toán",
            unpaid: "Chưa thanh toán",
            pending: "Đang xử lý",
            failed: "Thanh toán thất bại",
            refunded: "Đã hoàn tiền",
        };
        return paymentStatusMap[normalized] ?? value;
    };

    const actions = useMemo<OrderAction[]>(() => {
        switch (normalizedStatus) {
            case "placed":
                return [
                    { label: "Xác nhận", nextStatus: "confirmed", type: "primary" },
                    { label: "Hủy đơn", nextStatus: "cancelled", type: "danger" },
                ];
            case "confirmed":
                return [
                    { label: "Bắt đầu", nextStatus: "preparing", type: "primary" },
                    { label: "Hủy đơn", nextStatus: "cancelled", type: "danger" },
                ];
            case "preparing":
                return [
                    { label: "Hoàn thành", nextStatus: "served", type: "primary" },
                    { label: "Hủy đơn", nextStatus: "cancelled", type: "danger" },
                ];
            case "served":
                return [{ label: "Hoàn thành", nextStatus: "completed", type: "primary" }];
            default:
                return [];
        }
    }, [normalizedStatus]);

    const handleConfirmAction = async () => {
        if (!orderId || !pendingAction) return;
        setIsSubmitting(true);
        setActionError(null);
        try {
            await updateOrderStatus(orderId, pendingAction.nextStatus);
        } catch (err) {
            console.error(err);
            setActionError("Không thể cập nhật trạng thái đơn hàng.");
        } finally {
            setIsSubmitting(false);
            setPendingAction(null);
            onClose();
            onActionComplete?.();
        }
    };

    const orderItems = order?.order_items ?? order?.items ?? [];
    const payments = order?.payments ?? order?.order_payments ?? [];
    const statusHistories = order?.status_histories ?? order?.statusHistories ?? [];
    const createdAt = order?.created_at || order?.createdAt;
    const confirmedAt = order?.confirmed_at;
    const servedAt = order?.served_at;
    const closedAt = order?.closed_at;
    const openedAt = order?.opened_at;
    const total = order?.total ?? order?.total_price ?? 0;
    const formatTimestamp = (value?: string) => (value ? formatDate(value) : "--");
    const seatLocation = useMemo(() => {
        if (!order?.zone_name_snapshot && !order?.table_number_snapshot) {
            return "--";
        }
        return `${order?.zone_name_snapshot ?? "--"} - ${order?.table_number_snapshot ?? "--"}`;
    }, [order]);

    const renderOptions = (optionValues: any[]) => {
        if (!Array.isArray(optionValues) || optionValues.length === 0) {
            return null;
        }

        return (
            <ul className="list-unstyled mb-0 mt-1">
                {optionValues.map((optionValue: any) => (
                    <li key={optionValue.id ?? `${optionValue.option_id}-${optionValue.option_value_id}`}>
                        <small className="text-muted">
                            {optionValue.option_name_snapshot ?? optionValue.option_name ?? "Tuỳ chọn"}:{" "}
                            {optionValue.option_value_label_snapshot ?? optionValue.label ?? optionValue.name}
                            {optionValue.price_modifier_snapshot
                                ? ` (${toVND(Number(optionValue.price_modifier_snapshot))})`
                                : ""}
                            {optionValue.quantity && Number(optionValue.quantity) > 1
                                ? ` × ${optionValue.quantity}`
                                : ""}
                        </small>
                    </li>
                ))}
            </ul>
        );
    };

    const groupOptions = (options: any[] = []) => {
        return options.reduce((acc: Record<string, any[]>, cur) => {
            const key = cur.option_name_snapshot;
            if (!acc[key]) acc[key] = [];
            acc[key].push(cur);
            return acc;
        }, {});
    };

    const renderOrderItemOptions = (options: any[] = []) => {
        if (!options.length) return null;

        const groups = groupOptions(options);

        return (
            <div className="mt-1 small text-muted">
                {Object.entries(groups).map(([groupName, items]) => (
                    <div key={groupName} className="mb-1">
                        <div className="fw-medium">{groupName}:</div>

                        {items.map((opt, idx) => (
                            <div
                                key={idx}
                                className="d-flex justify-content-between ps-2"
                            >
                                <span>• {opt.option_value_label_snapshot}</span>

                                {Number(opt.price_modifier_snapshot) > 0 && (
                                    <span>
                                    +{toVND(
                                        Number(opt.price_modifier_snapshot)
                                    )}
                                </span>
                                )}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <React.Fragment>
            <Offcanvas
                fade
                isOpen={isOpen}
                direction="bottom"
                toggle={onClose}
                className="min-vh-100"
            >
                <OffcanvasHeader toggle={onClose} className="border-bottom">
                    Chi tiết đơn hàng {order?.order_number ?? orderId ?? ""}
                </OffcanvasHeader>
                <OffcanvasBody className="pb-5rem">
                    {error && (
                        <Alert color="danger" className="mb-3">
                            {error}
                        </Alert>
                    )}
                    {actionError && (
                        <Alert color="danger" className="mb-3">
                            {actionError}
                        </Alert>
                    )}
                    {loading?.getOrder && (
                        <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Đang tải...</span>
                            </div>
                        </div>
                    )}
                    {!loading?.getOrder && order && (
                        <React.Fragment>
                            <div className="card mb-3">
                                <div className="card-body">
                                    <h6 className="mb-3">Thông tin đơn hàng</h6>
                                    <div className="row g-2">
                                        <div className="col-6">
                                            <p className="mb-1 text-muted">Trạng thái</p>
                                            <p className={`fw-semibold mb-0 ${statusClassName}`}>{statusLabel}</p>
                                        </div>
                                        {/*<div className="col-6">*/}
                                        {/*    <p className="mb-1 text-muted">Thanh toán</p>*/}
                                        {/*    <p className="fw-semibold mb-0">{paymentStatus}</p>*/}
                                        {/*</div>*/}
                                        <div className="col-6">
                                            <p className="mb-1 text-muted">Tổng tiền</p>
                                            <p className="fw-semibold mb-0">{toVND(Number(total))}</p>
                                        </div>
                                        <div className="col-6">
                                            <p className="mb-1 text-muted">Khách muốn</p>
                                            <p className="fw-semibold mb-0">
                                                {orderTypeLabel}, {receivingPlaceLabel}
                                            </p>
                                        </div>
                                        <div className="col-6">
                                            <p className="mb-1 text-muted">Vị trí</p>
                                            <p className="fw-semibold mb-0">{seatLocation}</p>
                                        </div>
                                        {/*<div className="col-6">*/}
                                        {/*    <p className="mb-1 text-muted">Tạo lúc</p>*/}
                                        {/*    <p className="fw-semibold mb-0">{formatTimestamp(createdAt)}</p>*/}
                                        {/*</div>*/}
                                        <div className="col-6">
                                            <p className="mb-1 text-muted">Đặt lúc</p>
                                            <p className="fw-semibold mb-0">{formatTimestamp(openedAt)}</p>
                                        </div>
                                        <div className="col-6">
                                            <p className="mb-1 text-muted">Xác nhận lúc</p>
                                            <p className="fw-semibold mb-0">{formatTimestamp(confirmedAt)}</p>
                                        </div>
                                        <div className="col-6">
                                            <p className="mb-1 text-muted">Hoàn thành lúc</p>
                                            <p className="fw-semibold mb-0">{formatTimestamp(servedAt)}</p>
                                        </div>
                                        <div className="col-6">
                                            <p className="mb-1 text-muted">Đóng đơn lúc</p>
                                            <p className="fw-semibold mb-0">{formatTimestamp(closedAt)}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="card mb-3">
                                <div className="card-body">
                                    <h6 className="mb-3">Danh sách món</h6>

                                    {orderItems.length === 0 ? (
                                        <p className="text-muted mb-0">Chưa có món trong đơn.</p>
                                    ) : (
                                        <ul className="list-unstyled mb-0">
                                            {orderItems.map((item: any) => {
                                                const qty = Number(item.quantity || 0);
                                                const base = Number(item.base_price || 0);
                                                const total = Number(item.line_total || 0);

                                                return (
                                                    <li key={item.id} className="border-bottom pb-2 mb-2">
                                                        {/* Header */}
                                                        <div className="d-flex justify-content-between align-items-start">
                                                            <div>
                                                                <div className="fw-semibold">
                                                                    {item.name}
                                                                </div>
                                                                <small className="text-muted">
                                                                    {toVND(base)} × {qty}
                                                                </small>
                                                            </div>
                                                            <div className="fw-bold text-success">
                                                                {toVND(total)}
                                                            </div>
                                                        </div>

                                                        {/* Options */}
                                                        {renderOrderItemOptions(item.option_values)}

                                                        {/* Note */}
                                                        {item.note && (
                                                            <div className="small text-muted fst-italic mt-1 ps-2 border-start">
                                                                Ghi chú: {item.note}
                                                            </div>
                                                        )}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </div>
                            </div>

                            {/*<div className="card mb-3">*/}
                            {/*    <div className="card-body">*/}
                            {/*        <h6 className="mb-3">Thanh toán</h6>*/}
                            {/*        {payments.length === 0 ? (*/}
                            {/*            <p className="text-muted mb-0">Chưa có thông tin thanh toán.</p>*/}
                            {/*        ) : (*/}
                            {/*            <ul className="list-unstyled mb-0">*/}
                            {/*                {payments.map((payment: any) => (*/}
                            {/*                    <li key={payment.id} className="border-bottom pb-2 mb-2">*/}
                            {/*                        <div className="d-flex justify-content-between">*/}
                            {/*                            <div>*/}
                            {/*                                <p className="mb-1 fw-semibold text-capitalize">*/}
                            {/*                                    {payment.method ?? "--"}*/}
                            {/*                                </p>*/}
                            {/*                                <small className="text-muted">*/}
                            {/*                                    {formatPaymentStatus(payment.status)} •{" "}*/}
                            {/*                                    {payment.paid_at ? formatDate(payment.paid_at) : "--"}*/}
                            {/*                                </small>*/}
                            {/*                            </div>*/}
                            {/*                            <div className="fw-semibold">{toVND(Number(payment.amount ?? 0))}</div>*/}
                            {/*                        </div>*/}
                            {/*                    </li>*/}
                            {/*                ))}*/}
                            {/*            </ul>*/}
                            {/*        )}*/}
                            {/*    </div>*/}
                            {/*</div>*/}

                            <div className="card">
                                <div className="card-body">
                                    <h6 className="mb-3">Lịch sử trạng thái</h6>
                                    {statusHistories.length === 0 ? (
                                        <p className="text-muted mb-0">Chưa có lịch sử trạng thái.</p>
                                    ) : (
                                        <ul className="list-unstyled mb-0">
                                            {statusHistories.map((history: any) => (
                                                <li key={history.id} className="border-bottom pb-2 mb-2">
                                                    <p className="mb-1 fw-semibold">
                                                        {getOrderStatusLabel(history.from_status)} →{" "}
                                                        {getOrderStatusLabel(history.to_status)}
                                                    </p>
                                                    <small className="text-muted d-block">
                                                        {history.source ?? "--"} •{" "}
                                                        {history.created_at ? formatDate(history.created_at) : "--"}
                                                    </small>
                                                    {history.note && (
                                                        <small className="text-muted d-block">Ghi chú: {history.note}</small>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </React.Fragment>
                    )}
                </OffcanvasBody>
                {actions.length > 0 && (
                    <div className="fixed-bottom w-100 z-2 bg-white border-bottom">
                        <div className="card cart-amount-area">
                            <div className="card-body d-flex align-items-center justify-content-end gap-2">
                                {actions.map((action) => (
                                    <button
                                        key={action.nextStatus}
                                        type="button"
                                        className={`btn btn-${action.type}`}
                                        disabled={isSubmitting}
                                        onClick={() => {
                                            setPendingAction(action);
                                            onClose();
                                        }}
                                    >
                                        {isSubmitting && pendingAction?.nextStatus === action.nextStatus
                                            ? "Đang xử lý..."
                                            : action.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </Offcanvas>

            <ConfirmModal
                isOpen={Boolean(pendingAction)}
                typeConfirm={pendingAction?.type ?? "primary"}
                title="Xác nhận"
                content={
                    pendingAction
                        ? `Bạn có chắc muốn ${pendingAction.label.toLowerCase()} đơn hàng này?`
                        : ""
                }
                onConfirm={handleConfirmAction}
                onCancel={() => setPendingAction(null)}
            />
        </React.Fragment>
    );
};

export default OrderDetailOffcanvas;
